import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { URGENCY_LEVELS, urgencyOptionLabel } from '../urgency';
import {
  AlertTriangle, BarChart3, Rocket, SlidersHorizontal, Upload, Video, Hexagon } from 'lucide-react';
import { jobsApi, teamApi } from '../../../shared/services/api';
import { useAuth } from '../../auth';
import RichTextEditor from '../../../shared/components/RichTextEditor';
import { plainTextToHtml } from '../../../shared/utils/richText';
import { CURRENCIES, DEFAULT_CURRENCY, budgetPlaceholder } from '../../../shared/utils/budget';


export default function CreateJob() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // A team lead posts for someone in their team; a recruiter posts for themselves.
  const assigning = user?.role === 'team_lead';
  const [recruiters, setRecruiters] = useState([]);
  const [assignee, setAssignee] = useState(''); // '' = keep it with the team lead
  const [form, setForm] = useState({
    title: '',
    urgency: 'Medium',
    work_location: 'On-site', city: '', state: '', country: 'India',
    budget_min: '', budget_max: '',
    budget_currency: DEFAULT_CURRENCY,
    employment_type: 'Full-time',
    required_skills: [], preferred_skills: [],
    min_experience_years: '', education_required: '', certifications_required: [],
    job_description: '', status: 'Draft',
    resume_score_threshold: 75,
    interview_score_threshold: 60,
    shortlist_size: 10,
  });
  const [skillInput, setSkillInput] = useState('');
  const [certInput, setCertInput] = useState('');
  const [titleError, setTitleError] = useState('');
  const [jdImporting, setJdImporting] = useState(false);
  const [jdNotice, setJdNotice] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const [skillSuggestLoading, setSkillSuggestLoading] = useState(false);
  const debounceRef = useRef(null);
  const lastFetchedTitle = useRef('');

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  useEffect(() => {
    if (!assigning) return;
    teamApi.assignableRecruiters().then(setRecruiters).catch(() => setRecruiters([]));
  }, [assigning]);

  /**
   * Fill the form from an uploaded JD.
   *
   * Only fields the document actually yielded are written, so a second upload
   * or a correction already typed is never blanked out by an empty suggestion.
   */
  const importJd = async (file) => {
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    const type = (file.type || '').toLowerCase();
    if (!name.endsWith('.pdf') || (type && type !== 'application/pdf' && type !== 'application/x-pdf')) {
      setJdNotice({ ok: false, text: 'Upload a PDF job description.' });
      return;
    }
    setJdImporting(true);
    setJdNotice(null);
    try {
      const { fields, parsed_by: parsedBy } = await jobsApi.aiImportDescription(file);

      // Worked out here rather than inside the setForm updater: React runs the
      // updater after this function has moved on, and may run it twice, so a
      // list built in there is empty by the time the notice reads it.
      const usable = Object.entries(fields || {}).filter(([key, value]) => (
        key in form
        && value !== null && value !== ''
        && !(Array.isArray(value) && value.length === 0)
      )).map(([key, value]) => (
        key === 'job_description' ? [key, plainTextToHtml(value)] : [key, value]
      ));
      const filled = usable.map(([key]) => key);

      setForm(f => ({ ...f, ...Object.fromEntries(usable) }));
      if (filled.includes('title')) setTitleError('');
      setJdNotice({
        ok: true,
        text: filled.length
          ? `Filled ${filled.length} field${filled.length === 1 ? '' : 's'} from ${file.name}${parsedBy === 'fallback' ? ' (read from the text — AI parsing was unavailable)' : ''}. Check everything before publishing.`
          : `Nothing could be read from ${file.name}. Fill the form in by hand.`,
      });
    } catch (e) {
      setJdNotice({ ok: false, text: e.message || 'Could not read that file.' });
    } finally {
      setJdImporting(false);
    }
  };

  const addSkill = (skill, type = 'required') => {
    const s = skill.trim();
    if (!s) return;
    const key = type === 'required' ? 'required_skills' : 'preferred_skills';
    if (!form[key].includes(s)) update(key, [...form[key], s]);
    setSkillInput('');
  };

  const removeSkill = (skill, type) => {
    const key = type === 'required' ? 'required_skills' : 'preferred_skills';
    update(key, form[key].filter(s => s !== skill));
  };

  const addCert = (cert) => {
    const c = cert.trim();
    if (!c) return;
    if (!form.certifications_required.includes(c)) {
      update('certifications_required', [...form.certifications_required, c]);
    }
    setCertInput('');
  };

  const removeCert = (cert) => {
    update('certifications_required', form.certifications_required.filter(c => c !== cert));
  };

  // Debounced AI skill suggestions — fires 700ms after the user stops typing the title
  useEffect(() => {
    const title = form.title.trim();
    if (title.length < 3) {
      setSkillSuggestions([]);
      return;
    }
    if (title === lastFetchedTitle.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      lastFetchedTitle.current = title;
      setSkillSuggestLoading(true);
      try {
        const skills = await jobsApi.aiSuggestSkills(title);
        setSkillSuggestions(Array.isArray(skills) ? skills : []);
      } catch {
        setSkillSuggestions([]);
      } finally {
        setSkillSuggestLoading(false);
      }
    }, 700);
    return () => clearTimeout(debounceRef.current);
  }, [form.title]); // eslint-disable-line

  const recommendations = skillSuggestions.filter(s => !form.required_skills.includes(s));

  const handleAiGenerate = async () => {
    if (!form.title) return alert('Please enter a job title first');
    if (form.required_skills.length === 0) return alert('Add at least one required skill');
    setAiLoading(true);
    try {
      const result = await jobsApi.aiGenerateDescription(form.title, form.required_skills, form.employment_type);
      setAiSuggestion(result);
    } catch (e) {
      alert('AI generation failed: ' + e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    const esc = t => String(t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const list = items => (items || []).length
      ? `<ul>${items.map(i => `<li>${esc(i)}</li>`).join('')}</ul>`
      : '';
    const section = (heading, body) => body ? `<h2>${heading}</h2>${body}` : '';

    // HTML, because the editor is rich text now — markdown would show as
    // literal ## and bullets.
    const jd = [
      section('Role Summary', aiSuggestion.summary ? `<p>${esc(aiSuggestion.summary)}</p>` : ''),
      section('Key Responsibilities', list(aiSuggestion.responsibilities)),
      section('Requirements', list(aiSuggestion.requirements)),
      section('Nice to Have', list(aiSuggestion.nice_to_have)),
    ].join('');
    update('job_description', jd);
    setAiSuggestion(null);
  };

  const handleSubmit = async (status = 'Draft') => {
    const title = form.title.trim();
    if (!title) {
      setTitleError('Job title is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setTitleError('');

    setSaving(true);
    try {
      const payload = {
        ...form,
        title,
        status,
        budget_min: form.budget_min ? parseInt(form.budget_min) : null,
        budget_max: form.budget_max ? parseInt(form.budget_max) : null,
        min_experience_years: form.min_experience_years ? parseInt(form.min_experience_years) : null,
        resume_score_threshold: parseInt(form.resume_score_threshold),
        interview_score_threshold: parseInt(form.interview_score_threshold),
        shortlist_size: parseInt(form.shortlist_size),
        ...(assigning && assignee ? { assigned_to_id: assignee } : {}),
      };
      const job = await jobsApi.create(payload);
      navigate(`/jobs/${job.id}`);
    } catch (e) {
      alert('Error creating job: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Create Job Post</div>
          <div className="page-subtitle">Module 1 — Job Post Creation with AI Assistance</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={() => navigate('/jobs')}>Cancel</button>
          <button className="btn btn-secondary" onClick={() => handleSubmit('Draft')} disabled={saving}>Save Draft</button>
          <button className="btn btn-primary" onClick={() => handleSubmit('Active')} disabled={saving}>
            {saving ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Saving...</> : 'Publish Job'}
          </button>
        </div>
      </div>

      {assigning && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <div className="section-title" style={{ marginBottom: 0 }}>Assign to</div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Optional · you can change it later</span>
          </div>
          <select
            className="form-select"
            value={assignee}
            onChange={e => setAssignee(e.target.value)}
            style={{ maxWidth: 420 }}
          >
            <option value="">Myself ({user?.name})</option>
            {recruiters.map(r => <option key={r.id} value={r.id}>{r.name} ({r.email})</option>)}
          </select>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            {assignee
              ? 'Only this recruiter works the job and sees its candidates. You, your manager and the admin can view it.'
              : 'The job stays with you until you assign it to a recruiter in your team from the job page.'}
          </div>
        </div>
      )}

      {/* Start from a JD document */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="section-title" style={{ marginBottom: 0 }}>Start from a JD</div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Optional — fill the form from a file</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label
            htmlFor="jd-upload"
            className="btn btn-secondary btn-sm"
            style={{ cursor: jdImporting ? 'default' : 'pointer', opacity: jdImporting ? 0.7 : 1 }}
          >
            {jdImporting
              ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Reading…</>
              : <><Upload size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Upload JD</>}
          </label>
          <input
            id="jd-upload"
            type="file"
            accept="application/pdf,.pdf"
            disabled={jdImporting}
            style={{ display: 'none' }}
            onChange={e => { importJd(e.target.files?.[0]); e.target.value = ''; }}
          />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            PDF only. Everything it finds is a suggestion — review and
            correct the fields below before publishing.
          </span>
        </div>
        {jdNotice && (
          <div style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.55,
            background: jdNotice.ok ? 'rgba(5,150,105,0.07)' : 'rgba(239,68,68,0.07)',
            border: `1px solid ${jdNotice.ok ? 'var(--green)' : 'var(--red)'}`,
            color: 'var(--text-secondary)',
          }}>
            {jdNotice.text}
          </div>
        )}
      </div>

      {/* Basic Info */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="section-title">Basic Information</div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Job Title *</label>
            <input
              className="form-input"
              placeholder="e.g. Senior Python Developer"
              maxLength={200}
              value={form.title}
              onChange={e => { update('title', e.target.value); if (titleError) setTitleError(''); }}
              style={titleError ? { borderColor: 'var(--red)' } : undefined}
            />
            {titleError && (
              <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 5 }}>{titleError}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Employment Type</label>
            <select className="form-select" value={form.employment_type} onChange={e => update('employment_type', e.target.value)}>
              {['Full-time', 'Contract', 'Part-time', 'C2C'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Urgency / Hiring Priority</label>
            <select className="form-select" value={form.urgency} onChange={e => update('urgency', e.target.value)}>
              {URGENCY_LEVELS.map(l => (
                <option key={l.value} value={l.value}>{urgencyOptionLabel(l.value)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Location & Compensation */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="section-title">Location & Compensation</div>
        <div className="form-grid-3">
          <div className="form-group">
            <label className="form-label">Work Mode</label>
            <select className="form-select" value={form.work_location} onChange={e => update('work_location', e.target.value)}>
              {['On-site', 'Remote', 'Hybrid'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">City</label>
            <input className="form-input" placeholder="e.g. Bangalore" value={form.city} onChange={e => update('city', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Country</label>
            <input className="form-input" value={form.country} onChange={e => update('country', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Currency</label>
            <select className="form-select" value={form.budget_currency} onChange={e => update('budget_currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Annual Budget Min</label>
            <input className="form-input" type="number" placeholder={budgetPlaceholder(form.budget_currency, 'Min')} value={form.budget_min} onChange={e => update('budget_min', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Annual Budget Max</label>
            <input className="form-input" type="number" placeholder={budgetPlaceholder(form.budget_currency, 'Max')} value={form.budget_max} onChange={e => update('budget_max', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Min Experience (Years)</label>
            <input className="form-input" type="number" placeholder="e.g. 5" value={form.min_experience_years} onChange={e => update('min_experience_years', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Skills */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="section-title">Skills</div>
        <div className="form-group">
          <label className="form-label">Required Skills</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              className="form-input"
              placeholder="Add skill and press Enter"
              value={skillInput}
              onChange={e => setSkillInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSkill(skillInput)}
              style={{ flex: 1 }}
            />
            <button className="btn btn-secondary btn-sm" onClick={() => addSkill(skillInput)}>+ Add</button>
          </div>

          {/* AI skill suggestions based on job title */}
          <div style={{ marginBottom: 10, minHeight: 36 }}>
            {skillSuggestLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', color: 'var(--text-muted)', fontSize: 12 }}>
                <span className="spinner" style={{ width: 12, height: 12 }} />
                Generating skills for "{form.title}"…
              </div>
            ) : recommendations.length > 0 ? (
              <>
                <div style={{ fontSize: 11, color: 'var(--accent)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Hexagon size={20} strokeWidth={2} /> AI Suggested for "{form.title}"
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {recommendations.map(s => (
                    <button key={s} className="btn btn-ghost btn-sm"
                      style={{ padding: '3px 8px', fontSize: 11.5, borderColor: 'rgba(88,166,255,0.4)', color: 'var(--accent)' }}
                      onClick={() => addSkill(s)}>+ {s}</button>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>
                {form.title.trim().length >= 3
                  ? 'No suggestions yet — type more to refine'
                  : 'Type a job title above to get AI skill suggestions'}
              </div>
            )}
          </div>

          <div className="tags-wrap">
            {form.required_skills.map(s => (
              <span key={s} className="tag" style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => removeSkill(s, 'required')}>
                {s} ×
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Qualifications */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="section-title" style={{ marginBottom: 0 }}>Qualifications</div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Scored against each candidate on their profile
          </span>
        </div>

        <div className="form-group">
          <label className="form-label">Education</label>
          <input
            className="form-input"
            placeholder="e.g. B.Tech Computer Science"
            maxLength={200}
            value={form.education_required}
            onChange={e => update('education_required', e.target.value)}
          />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
            Leave blank if no specific degree matters — a candidate is only
            marked below the bar when there is a bar to be below.
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Certifications</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              className="form-input"
              placeholder="Add certification and press Enter"
              value={certInput}
              onChange={e => setCertInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCert(certInput)}
              style={{ flex: 1 }}
            />
            <button className="btn btn-secondary btn-sm" onClick={() => addCert(certInput)}>+ Add</button>
          </div>
          <div className="tags-wrap">
            {form.certifications_required.map(c => (
              <span key={c} className="tag" style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => removeCert(c)}>
                {c} ×
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Pipeline Pass Score Standards */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="section-title" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}><SlidersHorizontal size={13} strokeWidth={2} /> Pipeline Pass Score Standards</div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Set minimum scores to advance each stage</span>
        </div>
        <div style={{
          background: 'rgba(88,166,255,0.06)',
          border: '1px solid rgba(88,166,255,0.2)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 16,
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.6
        }}>
          <strong style={{ color: 'var(--accent)' }}>How it works:</strong> Candidates scoring below the Resume threshold are archived.
          Those who pass are auto-invited for an AI video interview via Microsoft Teams.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <label className="form-label" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}><BarChart3 size={13} strokeWidth={2} /> Resume Score Threshold (Filter 1)</label>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, color: form.resume_score_threshold >= 75 ? 'var(--green)' : form.resume_score_threshold >= 55 ? 'var(--accent)' : 'var(--yellow)' }}>
                {form.resume_score_threshold}/100
              </span>
            </div>
            <input type="range" min="35" max="95" step="5" value={form.resume_score_threshold}
              onChange={e => update('resume_score_threshold', parseInt(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--accent)' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              <span>35 — Lenient</span>
              <span style={{ color: 'var(--accent)' }}>Default: 75</span>
              <span>95 — Very Strict</span>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <label className="form-label" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}><Video size={13} strokeWidth={2} /> Interview Score Threshold (Filter 2)</label>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, color: form.interview_score_threshold >= 70 ? 'var(--green)' : form.interview_score_threshold >= 50 ? 'var(--accent)' : 'var(--yellow)' }}>
                {form.interview_score_threshold}/100
              </span>
            </div>
            <input type="range" min="30" max="90" step="5" value={form.interview_score_threshold}
              onChange={e => update('interview_score_threshold', parseInt(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--purple)' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              <span>30 — Lenient</span>
              <span style={{ color: 'var(--purple)' }}>Default: 60</span>
              <span>90 — Very Strict</span>
            </div>
          </div>
        </div>
      </div>

      {/* Job Description */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="section-title" style={{ marginBottom: 0 }}>Job Description</div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleAiGenerate}
            disabled={aiLoading}
          >
            {aiLoading ? (
              <>
                <span
                  className="spinner"
                  style={{ width: 12, height: 12 }}
                />
                Generating...
              </>
            ) : (
              <>
                <Hexagon size={12} strokeWidth={2} />
                AI Generate
              </>
            )}
          </button>
        </div>

        {aiSuggestion && (
          <div style={{ background: 'var(--accent-glow)', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, marginBottom: 8 }}>⬡ AI Suggestion Ready</div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>{aiSuggestion.summary}</p>
            {(aiSuggestion.bias_flags || []).length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--yellow)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}><AlertTriangle size={12} strokeWidth={2} /> Bias detected: {aiSuggestion.bias_flags.join(', ')}</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={applyAiSuggestion}>Apply to Editor</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAiSuggestion(null)}>Dismiss</button>
            </div>
          </div>
        )}

        <RichTextEditor
          value={form.job_description}
          onChange={html => update('job_description', html)}
          placeholder="Write or paste the job description here. Use the toolbar or AI Generate for a draft."
          minHeight={300}
        />
      </div>

      {/* Bottom action bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 10,
        padding: '16px 20px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        marginBottom: 32,
      }}>
        <button className="btn btn-ghost" onClick={() => navigate('/jobs')}>Cancel</button>
        <button className="btn btn-secondary" onClick={() => handleSubmit('Draft')} disabled={saving}>
          Save as Draft
        </button>
        <button className="btn btn-primary" onClick={() => handleSubmit('Active')} disabled={saving}
          style={{ minWidth: 130, fontSize: 14 }}>
          {saving
            ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Publishing...</>
            : <><Rocket size={14} strokeWidth={2} /> Publish Job</>}
        </button>
      </div>
    </div>
  );
}
